
// Component here uses ES6 destructuring syntax in import, what is means is "retrieve the property 'Component' off of the object exported from the 'react'"
import React, { PropTypes } from 'react';
import { connect } from 'react-redux';

// utilities
import { get, filter, merge, orderBy, find, debounce } from 'lodash';
import shortId from 'shortid';
import cx from 'classnames';

// components
import TextArea from '../../../../../../common/components/c_TextArea';
import FlatButton from '../../../../../../common/components/c_FlatButton';
import ProgressComponentList from '../../../../../../common/components/C_ProgressComponentList';
import ChevronIcon from '../../../../../../common/svgs/ChevronIcon';
import Experience from './Experience';
import AccordionList, {AccordionListItem} from '../../../../../../common/components/AccordionList';
import { resumeDate } from '../../../../../../lib/prettyDate';
import * as ui from '../../../../../../redux/actionCreators/sync/ui';

// Default Labels
const formHeading = 'Work Experience';
const formText = `
  Please give your full employment record covering the last twenty-five (25) years.
  We have your permission to contact references unless you indicate "No" for a specific employer.
  If you have letters of reference, you will have the opportunity to attach them from the File Uploads section towards the end
  of the application.
`;

const adminScript = 'Great. Let’s review your work experience.';
const adminInstructions = `
Instructions: Review your notes about the career summary and work experience with the applicant and the resume that was generated from the information. Review the reason for any “NO” for Permission to Contact Employer.
`;
const adminInstrutionsItems = [
  'If the resume needs to be corrected and/or updated, explain:',
  'If the applicant did not enter a gap, ask:',
  'If the applicant’s end date for the most current job is over four months, ask the applicant the questions below:'
];
// End of Default Labels

// needed to replace '\n' with actual newline
const newLine = /\\n/g;

@connect(store => ({
  labels: get(store.labels, 'Application.experience', {}),
}), null, null, {withRef: true})
export default class WorkExperience extends ProgressComponentList {
  static propTypes = {
    dispatch        : PropTypes.func.isRequired,
    workExperiences : PropTypes.object.isRequired,
    profile         : PropTypes.object.isRequired,
    profileId       : PropTypes.number.isRequired,
    model           : PropTypes.string.isRequired,
    staticData      : PropTypes.object.isRequired,
    tempState       : PropTypes.object.isRequired,
    validation      : PropTypes.object.isRequired,
    errorUpdate     : PropTypes.func.isRequired,
    onSave          : PropTypes.func.isRequired,
    setErrorMessage : PropTypes.func.isRequired,
    labels          : PropTypes.object.isRequired
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      has25ExperienceYears: false,
      selectedClientId: null,
      showErrors: false,
    };
  }

  handleTextAreaUI(prop) {
    return {
      // need to replace '\n' with actual newline
      defaultValue: (this.props.profile[prop] || ' ').replace(newLine, '\n'),
      onBlur: (event, val) => this.updateTempState('profile', 'careerSummary', event, val || ' ')
    };
  }

  adminQuestionProperties = (model, question, label) => ({
    defaultValue: get(this.props.tempState[model], question, get(this.props[model], question, '')),
    floatingLabelText: label,
    capitalizedSentences: true,
    validation: this.props.validation[question],
    onErrorChange: this.props.errorUpdate,
    onBlur: (event, val) => {
      this.updateTempState(model, question, event, val);
    }
  })

  handleClickExperience = (clientId) => {
    this.setState({selectedClientId: clientId, showErrors: false});
    this.props.dispatch(ui.hideControls());
  }

  handleCancelExperience = () => {
    this.setState({selectedClientId: null}, () => this.props.updateTempState(`${this.props.model}`, '', null, {}));
    this.props.dispatch(ui.showControls());
  }

  handleSave = (clientId) => {
    if (this.props.onSave()) {
      this.setState({selectedClientId: null});
      this.props.dispatch(ui.showControls());
    }
    else {
      this.setState({showErrors: true});
    }
  }

  handleSaveExperience = (clientId) =>
    () => {
      debounce(this.handleSave(clientId), 200);
  }

  handleAddExperience = () => {
    this.addListItem(this.handleClickExperience);
  }

  handleRemoveExperience = (clientId) =>
    () => {
      this.removeListItem(clientId);
      this.props.dispatch(ui.showControls());
  }

  parseDate(dateString) {
    const dateArray = dateString.split('/');
    const test = new Date(dateArray[1], dateArray[0]-1);
    return test;
  }

  // Override ValidatedForm.validateForm()
  validateForm() {
    const isValid = filter(this.refs, ref => ref.getWrappedInstance().validateForm)
      .reduce((valid, section, index) => section.getWrappedInstance().validateForm() && valid, true);
    return isValid;
  }

  getLabel(name, defaultValue = '') {
    return get(this.props, `labels.${name}`, defaultValue);
  }

  get newExperience() {
    return {
      isNew: true,
      clientId: this.newClientId
    };
  }

  get newClientId() {
    return `${this.getIncrementedChar()}${shortId.generate()}`;
  }

  get formHeading() {
    return this.getLabel('formHeading', formHeading);
  }

  get formInstructions() {
    return this.getLabel('formInstructions', formText);
  }

  get adminScript() {
    return this.getLabel('adminScript', adminScript);
  }

  get adminInstructions() {
    return this.getLabel('adminInstructions', adminInstructions);
  }

  get adminItems() {
    return this.getLabel('adminItems', adminInstrutionsItems);
  }

  renderAdminAccordion(workExperiences) {
    if (!workExperiences.length) {
      return;
    }

    workExperiences = orderBy(workExperiences, ['current', 'employmentEnd', 'employmentStart'], ['desc', 'desc', 'desc']);
    return (<AccordionList>
      { workExperiences.map((experience, index, arr) =>
        <AccordionListItem
          key={experience.clientId}
          showCheckMark={ false }
          toggleOpenedText='Close'
          hideAllOnOpen={ false }
          title={
            <div data-id={experience.employerName}
                className='experience-header'>
                <div className='text'>
                  <b>{experience.employerName}. </b>{experience.title}. ({resumeDate(experience.employmentStart)} - {experience.current ? 'Current' : resumeDate(experience.employmentEnd)})
                </div>
            </div>
          }
        >
          <Experience
            ref={experience.clientId}
            index={index}
            experience={experience}
            tempState={this.props.tempState}
            model={this.props.model}
            onChange={this.onChange.bind(this)}
            removeListItem={this.handleRemoveExperience(experience.clientId)}
            showRemove={arr.length > 1}
            staticData={this.props.staticData}
            updateTempState={this.props.updateTempState}
            validation={this.props.validation}
            validateUpdate={this.validateUpdate}
            adminView={this.props.adminView}
            submitted={this.props.submitted}
          />
          </AccordionListItem>
      ) }
      </AccordionList>
    );
  }

  renderRegularList(workExperiences) {
    workExperiences = orderBy(workExperiences, ['current', 'employmentEnd', 'employmentStart'], ['desc', 'desc', 'desc']);
    return (
      <div className='accordion-container'>
      {
        workExperiences.map((experience, index, arr) =>
        <div className={cx('accordion-panel-container', {'applicant-view': !this.props.adminView})}>
          <div className='accordion-panel-header' onClick={this.handleClickExperience.bind(this, experience.clientId)}>
            <div className='left'>
              <div data-id={experience.employerName}
                  className='experience-header'>
                  <div className='text'>
                    <b>{experience.employerName}. </b>{experience.title}. ({resumeDate(experience.employmentStart)} - {experience.current ? 'Current' : resumeDate(experience.employmentEnd)})
                  </div>
              </div>
            </div>
            <div className='right'>
              <div className='accordion-toggle'>
                <div className='accordion-toggle-text noTextSelect'>View</div>
              </div>
              <div className='accordion-caret-container'>
                <div className='accordion-toggle-caret chevron toggled'><ChevronIcon/></div>
              </div>
            </div>
          </div>
        </div>)
      }
      </div>
    );
  }

  render() {
    const workExperiences = filter(merge({}, this.props[this.props.model], this.props.tempState[this.props.model]), (exp) => exp !== Symbol.for('__delete'));
    const selectedExperience = find(workExperiences, w => w.clientId === this.state.selectedClientId);

    return (
      <div className={cx('application-section-container expanded-accordion-content clearfix')}>
        <div className='form-heading'>
          {this.formHeading}
        </div>
        <div className='form-text'>
          {(this.props.adminView && this.props.script) && <div className='speaking-callout'>{adminScript}</div>}
          {!(this.props.adminView) && !this.props.submitted && this.formInstructions}
        </div>
        {
          (this.props.adminView)
          &&
          <div>
            <a target='_blank' className='view-resume-btn' href={`/admin/Qualified/${this.props.profile.id}/Profile/resume`}>
              <FlatButton className='view-resume-btn'
                          label='View Resume'
                          style={{
                            display: 'block',
                            width: 'auto',
                            margin: '50px auto'
                          }}/>
            </a>
            <div className='career-summary-container'>
              <div className='title'>Career Summary</div>
              <TextArea
                className='career-summary-text'
                data-id='Career Summary textarea'
                maxLength={800}
                showCharacterCount
                capitalizedSentences
                exampleText={'Seasoned insurance professional with over twenty-five years of property and casualty ' +
                  'insurance brokerage experience servicing and managing mid-commercial accounts. Proficient in all ' +
                  'lines of coverage with a specialty in large real estate accounts. Effectively managed client ' +
                  'expectations, building and maintaining strong, positive relationships with clients and ' +
                  'underwriters. Successfully upsold and cross-sold policies to improve clients’ insurance ' +
                  'programs. Managed multiple priorities, delivering timely and accurate work products with ' +
                  'a customer service and client retention focus. Developed strong computer skills and the ' +
                  'ability to navigate through multiple systems. Have focused on professional growth throughout ' +
                  'my career including obtaining the CIC designation.'}
                {...this.handleTextAreaUI('careerSummary')}
              />
            </div>
          </div>
        }
        <div className='work-experience-container'>
          { this.props.adminView ? this.renderAdminAccordion(workExperiences) : (selectedExperience ? null : this.renderRegularList(workExperiences)) }
          { selectedExperience && <Experience
              key={selectedExperience.clientId}
              ref={selectedExperience.clientId}
              index={-1}
              experience={selectedExperience}
              model={this.props.model}
              onChange={this.onChange.bind(this)}
              removeListItem={this.handleRemoveExperience(selectedExperience.clientId)}
              saveListItem={this.handleSaveExperience(selectedExperience.clientId)}
              showRemove={false}
              staticData={this.props.staticData}
              updateTempState={this.props.updateTempState}
              handleCancelExperience={this.handleCancelExperience}
              validation={this.props.validation}
              validateUpdate={this.validateUpdate}
              submitted={this.props.submitted}
              showErrors={this.state.showErrors}
            />
          }
          { !selectedExperience
            &&
            <FlatButton
              onClick={this.handleAddExperience}
              className='add-new-experience'
              label='Add Employer'
              style={{
                display: 'inline-block',
                width: '296px',
                height: '41px',
                fontSize: '12px',
                paddingLeft: '30px',
                paddingRight: '30px',
                backgroundColor: '#2eb0e3',
                boxShadow: '0 2px 1px 0 rgba(0,0,0,.41), 0 0 2px 0 rgba(0,0,0,.12)',
                borderRadius: '2px',
                border: '0',
                color: '#ffffff',
              }}
              hoverStyle={{
                color: '#ffffff'
              }}
            />
          }
        </div>
        {
          this.props.adminView
          &&
          <div className='explanation-of-wahve'>
            <div className='callout above-form-contents'>
              {this.adminInstructions}
              <ol className='instructions-list work-experience-instructions'>
                {
                  this.adminItems.map((item, index) =>
                    <li className='instructions-list-item' key={index}>
                      {item}
                      { index === 0 && this.props.script
                        &&
                        <div className='speaking-callout'>After we complete this interview, you will need to log back into your record and correct your application so that the resume is accurate.</div>
                      }
                      { index === 1
                        &&
                        <div className='questions'>
                          <div className='title'>Please explain the gap as it is helpful to us if client asks us about it?</div>
                          <TextArea
                            data-id={'Please explain the gap as it is helpful to us if client asks us about it?'}
                            {...this.adminQuestionProperties('profile', 'gapExplanation', 'Please explain the gap as it is helpful to us if client asks us about it?')}
                          />
                        </div>
                      }
                      { index === 2
                        &&
                        <div className='questions'>
                          <div className='title'>What have you been doing to stay current?</div>
                          <TextArea
                            data-id={'What have you been doing to stay current?'}
                            {...this.adminQuestionProperties('profile', 'howStayedCurrentWithWork', 'What have you been doing to stay current?')}
                          />
                          <div className='title'>Have you been doing any type of work?</div>
                          <TextArea
                            data-id={'Have you been doing any type of work?'}
                            {...this.adminQuestionProperties('profile', 'doingAnyTypeOfWork', 'Have you been doing any type of work?')}
                          />
                        </div>
                      }
                    </li>
                  )
                }
              </ol>
            </div>
          </div>
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./shared/react/r_ApplicantDashboard/f_Application/r_ApplicationForm/Experience/c_WorkExperience/index.js